import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Building2, Loader2, Pencil, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';

function formatApiError(err, fallback = 'Failed to save settlement bank') {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error ?? data;
  if (detail == null && err?.message === 'Network Error') return 'Unable to reach the server. Please check your connection and try again.';
  if (status >= 500 && !detail) return 'The server could not complete this request right now. Please try again in a moment.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || item?.message || item?.detail || JSON.stringify(item)).filter(Boolean).join(' ');
  if (detail?.message) return detail.message;
  if (detail?.error) return typeof detail.error === 'string' ? detail.error : JSON.stringify(detail.error);
  return fallback;
}

const DEFAULT_FORM = {
  country: '',
  country_code: '',
  currency: 'UGX',
  account_type: 'bank_account',
  bank_name: '',
  bank_code: '',
  branchesText: '',
  requires_swift_code: false,
  is_mobile_money: false,
  display_order: 999,
  active: true,
};

export default function SettlementBanks() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState('');
  const [importText, setImportText] = useState('');

  const loadBanks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settlement/banks', { params: { search, active_only: false } });
      setBanks(data?.banks || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load settlement banks'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanks();
  }, [search]);

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setEditingId('');
  };

  const openEdit = (bank) => {
    setEditingId(bank.id);
    setForm({
      country: bank.country || '',
      country_code: bank.country_code || '',
      currency: bank.currency || 'UGX',
      account_type: bank.account_type || 'bank_account',
      bank_name: bank.bank_name || '',
      bank_code: bank.bank_code || '',
      branchesText: Array.isArray(bank.branches)
        ? bank.branches.map((branch) => branch.branch_name).join('\n')
        : '',
      requires_swift_code: !!bank.requires_swift_code,
      is_mobile_money: !!bank.is_mobile_money,
      display_order: Number(bank.display_order ?? 999),
      active: !!bank.active,
    });
  };

  const saveBank = async () => {
    if (!form.country || !form.bank_name) {
      toast.error('Country and bank name are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        branches: form.branchesText,
      };
      if (editingId) {
        await api.put(`/admin/settlement/banks/${editingId}`, payload);
        toast.success('Settlement bank updated');
      } else {
        await api.post('/admin/settlement/banks', payload);
        toast.success('Settlement bank created');
      }
      resetForm();
      await loadBanks();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteBank = async (bankId) => {
    if (!window.confirm('Delete this settlement bank?')) return;
    try {
      await api.delete(`/admin/settlement/banks/${bankId}`);
      toast.success('Settlement bank deleted');
      await loadBanks();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete settlement bank'));
    }
  };

  const importBanks = async () => {
    let items = [];
    try {
      const parsed = JSON.parse(importText);
      items = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      toast.error('Import expects a JSON array or object.');
      return;
    }
    setImporting(true);
    try {
      await api.post('/admin/settlement/banks/import', { items });
      toast.success('Settlement banks imported');
      setImportText('');
      await loadBanks();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to import settlement banks'));
    } finally {
      setImporting(false);
    }
  };

  const summary = {
    total: banks.length,
    active: banks.filter((bank) => bank.active).length,
    mobile: banks.filter((bank) => bank.is_mobile_money).length,
    swift: banks.filter((bank) => bank.requires_swift_code).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Settlement Banks</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage countries, currencies, bank codes and branches for settlement forms.</p>
        </div>
        <Button onClick={loadBanks} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50">
          <RefreshCw size={16} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Card className="rounded-2xl border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Total Banks</p><p className="text-2xl font-bold text-slate-900 mt-1">{summary.total}</p></CardContent></Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Active</p><p className="text-2xl font-bold text-slate-900 mt-1">{summary.active}</p></CardContent></Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Mobile Money</p><p className="text-2xl font-bold text-slate-900 mt-1">{summary.mobile}</p></CardContent></Card>
        <Card className="rounded-2xl border border-slate-200 shadow-sm"><CardContent className="p-4"><p className="text-xs text-slate-500">Swift Required</p><p className="text-2xl font-bold text-slate-900 mt-1">{summary.swift}</p></CardContent></Card>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{editingId ? 'Edit Settlement Bank' : 'Add Settlement Bank'}</CardTitle>
          <p className="text-xs text-slate-500">Use one branch per line or comma separated values. Branch codes are optional and will be saved as blank if omitted.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))} className="mt-1" data-testid="settlement-bank-country" /></div>
            <div><Label>Country Code</Label><Input value={form.country_code} onChange={(e) => setForm((p) => ({ ...p, country_code: e.target.value.toUpperCase() }))} className="mt-1" data-testid="settlement-bank-country-code" /></div>
            <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))} className="mt-1" data-testid="settlement-bank-currency" /></div>
            <div>
              <Label>Account Type</Label>
              <Select value={form.account_type} onValueChange={(v) => setForm((p) => ({ ...p, account_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_account">Bank Account</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={(e) => setForm((p) => ({ ...p, bank_name: e.target.value }))} className="mt-1" data-testid="settlement-bank-name" /></div>
            <div><Label>Bank Code</Label><Input value={form.bank_code} onChange={(e) => setForm((p) => ({ ...p, bank_code: e.target.value }))} className="mt-1" data-testid="settlement-bank-code" /></div>
            <div><Label>Display Order</Label><Input type="number" value={form.display_order} onChange={(e) => setForm((p) => ({ ...p, display_order: parseInt(e.target.value || '0', 10) || 0 }))} className="mt-1" data-testid="settlement-bank-order" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Branches</Label>
              <textarea
                value={form.branchesText}
                onChange={(e) => setForm((p) => ({ ...p, branchesText: e.target.value }))}
                className="mt-1 w-full min-h-[140px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                placeholder="AIRTEL&#10;MTN"
                data-testid="settlement-bank-branches"
              />
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <input type="checkbox" checked={form.requires_swift_code} onChange={(e) => setForm((p) => ({ ...p, requires_swift_code: e.target.checked }))} className="w-4 h-4" data-testid="settlement-bank-swift" />
                <span className="text-sm text-slate-700">Swift code required</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <input type="checkbox" checked={form.is_mobile_money} onChange={(e) => setForm((p) => ({ ...p, is_mobile_money: e.target.checked }))} className="w-4 h-4" data-testid="settlement-bank-mobile" />
                <span className="text-sm text-slate-700">Mobile money option</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} className="w-4 h-4" data-testid="settlement-bank-active" />
                <span className="text-sm text-slate-700">Active</span>
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={saveBank} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="settlement-bank-save-btn">
              {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Plus size={16} className="mr-1.5" />}
              {editingId ? 'Update Bank' : 'Save Bank'}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm} className="border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="settlement-bank-cancel-btn">Cancel Edit</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Import Banks</CardTitle>
          <p className="text-xs text-slate-500">Paste a JSON array of settlement bank objects to add or update many banks at once.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            className="w-full min-h-[140px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            placeholder='[{"country":"Uganda","country_code":"UG","currency":"UGX","account_type":"mobile_money","bank_name":"Mobile money","bank_code":"MOBILE_MONEY","branches":["AIRTEL","MTN"]}]'
            data-testid="settlement-bank-import-text"
          />
          <Button onClick={importBanks} disabled={importing} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="settlement-bank-import-btn">
            {importing ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Upload size={16} className="mr-1.5" />}
            Import Banks
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Settlement Banks List</CardTitle>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search banks, branches, currency..." className="w-full sm:w-80" data-testid="settlement-bank-search" />
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <Table className="min-w-[1000px]">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Country</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Currency</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Bank Name</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Bank Code</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Branches</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Swift</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {banks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-10 text-center text-sm text-slate-500">
                      No settlement banks found.
                    </TableCell>
                  </TableRow>
                ) : banks.map((bank) => (
                  <TableRow key={bank.id} className="hover:bg-slate-50/50">
                    <TableCell className="text-sm text-slate-700">{bank.country || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-700">{bank.currency || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-700 capitalize">{bank.account_type || '-'}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-900">{bank.bank_name}</TableCell>
                    <TableCell className="text-sm text-slate-500 mono-nums">{bank.bank_code || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{(bank.branches || []).map((branch) => branch.branch_name).join(', ') || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{bank.requires_swift_code ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bank.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                        {bank.active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="flex flex-wrap gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(bank)} data-testid={`settlement-bank-edit-${bank.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteBank(bank.id)} className="text-red-500 hover:text-red-700" data-testid={`settlement-bank-delete-${bank.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
